import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useMemo, useState } from 'react';
import { Popover, PopoverPanel } from '@headlessui/react';
import { usePremadeAnswers as usePremadeAnswersApi } from 'flow/hooks/api/usePremadeAnswer';
import useClickOutsideHandler from 'flow/hooks/useClickOutsideHandler';
import usePremadeAnswers from 'flow/hooks/usePremadeAnswers';
import { insertPremadeAnswer } from 'flow/utils/premade-answer-utils';
import { twMerge } from 'tailwind-merge';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Component is a popover showing the premade answers we could potentially insert.
export var PremadeAnswerInserter = function PremadeAnswerInserter(_ref) {
  var state = _ref.state,
    updateState = _ref.updateState,
    updateMessage = _ref.updateMessage;
  var _usePremadeAnswersApi = usePremadeAnswersApi(),
    allPremadeAnswers = _usePremadeAnswersApi.data;
  var _usePremadeAnswers = usePremadeAnswers(),
    substitutePlaceholders = _usePremadeAnswers.substitutePlaceholders;

  // Apply any filtering.
  var filteredPremadeAnswers = useMemo(function () {
    var _allPremadeAnswers$fi;
    return allPremadeAnswers === null || allPremadeAnswers === void 0 || (_allPremadeAnswers$fi = allPremadeAnswers.filter(function (answer) {
      return state.open && (!answer.type || answer.type === 'PREMADE_ANSWER') && answer.name.toLowerCase().startsWith(state.filter.toLowerCase());
    })) === null || _allPremadeAnswers$fi === void 0 ? void 0 : _allPremadeAnswers$fi.sort(function (a, b) {
      return a.name < b.name ? -1 : 1;
    });
  }, [allPremadeAnswers, state.open, state.open ? state.filter : '']);

  // Use maximally 60% of the viewport height for answers.
  var maxAnswers = Math.ceil(window.innerHeight * 0.6 / 60);

  // The displayed answers (accounting for arrow key handling), as well as the current offset
  // of the answers we show (to give nicer UX when we use arrow keys by minimizing the amount
  // of scrolling we do).
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    displayedPremadeAnswers = _useState2[0],
    setDisplayedPremadeAnswers = _useState2[1];
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    offsetStart = _useState4[0],
    setOffsetStart = _useState4[1];
  useEffect(function () {
    var _state$proposedInsert;
    // If no answers, nothing to show.
    if (!state.open || filteredPremadeAnswers.length === 0) {
      setDisplayedPremadeAnswers(undefined);
      setOffsetStart(0);
      return;
    }

    // Make sure insertion offset is in range; reset to 0 if not (or if unset), and determine
    // correct insertion candidate. Update state if needed.
    var newInsertionOffset = Math.min(Math.max(state.insertionOffset || 0, 0), filteredPremadeAnswers.length - 1);
    var newProposedInsertion = filteredPremadeAnswers[newInsertionOffset];
    if (state.insertionOffset !== newInsertionOffset || ((_state$proposedInsert = state.proposedInsertion) === null || _state$proposedInsert === void 0 ? void 0 : _state$proposedInsert.key) !== newProposedInsertion.key) {
      updateState(_objectSpread(_objectSpread({}, state), {}, {
        insertionOffset: newInsertionOffset,
        proposedInsertion: newProposedInsertion
      }));
    }

    // Make sure the offset start doesn't result in us showing less than the
    // desired number of mentions, and that the mention we are currently on
    // is in the window.
    var newOffsetStart = offsetStart;
    if (filteredPremadeAnswers.length - offsetStart < maxAnswers) {
      newOffsetStart = 0;
    }
    if (newInsertionOffset < offsetStart) {
      // We're too far down the list; move up.
      newOffsetStart = newInsertionOffset;
    } else if (newInsertionOffset >= offsetStart + maxAnswers) {
      // We're too far up the list; move down.
      newOffsetStart = newInsertionOffset - maxAnswers + 1;
    }
    setDisplayedPremadeAnswers(filteredPremadeAnswers.slice(newOffsetStart, newOffsetStart + maxAnswers));
    if (newOffsetStart !== offsetStart) {
      setOffsetStart(newOffsetStart);
    }
  }, [filteredPremadeAnswers === null || filteredPremadeAnswers === void 0 ? void 0 : filteredPremadeAnswers.length, state.open ? state.insertionOffset : -1, offsetStart]);
  var popoverRef = useClickOutsideHandler(function () {
    return updateState({
      open: false
    });
  });
  return /*#__PURE__*/_jsx(Popover, {
    children: state.open && displayedPremadeAnswers && /*#__PURE__*/_jsx(PopoverPanel, {
      "static": true,
      className: "absolute start-3 top-0 z-40 w-full -translate-y-full pe-8 outline-hidden",
      ref: popoverRef,
      children: /*#__PURE__*/_jsx("div", {
        className: "w-full rounded-sm bg-white p-2 shadow-lg",
        children: displayedPremadeAnswers.map(function (answer, index) {
          var _state$proposedInsert2;
          return /*#__PURE__*/_jsxs("div", {
            className: twMerge('flex cursor-pointer flex-col p-2', state.open && ((_state$proposedInsert2 = state.proposedInsertion) === null || _state$proposedInsert2 === void 0 ? void 0 : _state$proposedInsert2.key) === answer.key ? 'bg-gray-200' : 'hover:bg-gray-100'),
            onMouseEnter: function onMouseEnter() {
              return updateState(_objectSpread(_objectSpread({}, state), {}, {
                insertionOffset: offsetStart + index
              }));
            },
            onWheel: function onWheel(e) {
              var notches = Math.ceil(Math.abs(e.deltaY / 120)) * (e.deltaY < 0 ? -1 : 1);
              var maxOffsetStart = Math.max(filteredPremadeAnswers.length - maxAnswers, 0);
              setOffsetStart(Math.min(Math.max(offsetStart + notches, 0), maxOffsetStart));
            },
            onClick: function onClick() {
              return insertPremadeAnswer(substitutePlaceholders(answer.message), updateState, updateMessage);
            },
            children: [/*#__PURE__*/_jsx("p", {
              className: "text-sm/5 text-gray-800",
              children: answer.name
            }), /*#__PURE__*/_jsx("p", {
              className: "truncate overflow-hidden text-xs/4 whitespace-nowrap text-gray-500",
              children: substitutePlaceholders(answer.message)
            })]
          }, answer.key);
        })
      })
    })
  });
};